import { useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { serialize } from "object-to-formdata"
import { updateExisting } from "store/clients"
import { toaster } from "components/common/Toast"

/**
 * Custom hook to manage record editing functionality
 * Follows Single Responsibility Principle by isolating edit logic
 *
 * @param {number|string} importId - The import ID for updating records
 * @returns {Object} Edit state and handlers
 */
const useRecordEdit = (importId) => {
  const dispatch = useDispatch()
  const [editingId, setEditingId] = useState(null)

  /**
   * Handles click on edit button
   * Memoized to prevent unnecessary re-creation
   */
  const handleEditClick = useCallback((rowNumber, record, form) => {
    setEditingId(rowNumber)
    form.setFieldsValue({
      first_name: record.first_name,
      last_name: record.last_name,
      email: record.email,
      phone: record.phone_number || "",
      gender: record.gender || "",
      birthday: record.birthday || ""
    })
  }, [])

  /**
   * Handles save of edited record
   * Memoized to prevent unnecessary re-creation
   */
  const handleSave = useCallback(
    async (rowNumber, record, form) => {
      const formValues = form.getFieldsValue()
      const recordData = record.row || {}

      const guide_client = {
        ...formValues,
        row: rowNumber,
        id: importId,
        first_name: formValues.first_name || recordData.first_name,
        last_name: formValues.last_name || recordData.last_name,
        email: formValues.email || recordData.email,
        phone_number: formValues.phone_number || recordData.phone_number,
        gender: formValues.gender || recordData.gender,
        birthday: formValues.birthday || recordData.birthday,
        country_code: formValues.country_code || recordData.country_code,
        failed: true
      }

      const formData = serialize({ guide_client }, { indices: true })

      try {
        const result = await dispatch(updateExisting(formData, importId))

        if (result?.message) {
          toaster.success(result.message)
        }

        setEditingId(null)
      } catch (error) {
        console.error("Error updating client:", error)
      }
    },
    [dispatch, importId]
  )

  /**
   * Handles cancel of edit
   * Memoized to prevent unnecessary re-creation
   */
  const handleCancel = useCallback(() => {
    setEditingId(null)
  }, [])

  return {
    editingId,
    handleEditClick,
    handleSave,
    handleCancel
  }
}

export default useRecordEdit
